import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  toggleLocationStatus,
} from "../../../slice/location/locationslice";

/* ─── Load Leaflet CSS once ─── */
const ensureLeafletLoaded = () => {
  if (!document.getElementById("leaflet-css")) {
    const link = document.createElement("link");
    link.id   = "leaflet-css";
    link.rel  = "stylesheet";
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(link);
  }
  return new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }
    const existing = document.querySelector('script[src*="leaflet.min.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
};

/* ─── tiny reusable label ─── */
const FieldLabel = ({ children, required }) => (
  <label style={{
    display: "block", fontSize: 11, fontWeight: 700,
    letterSpacing: 1, color: "#888", marginBottom: 8, textTransform: "uppercase",
  }}>
    {children}{required && <span style={{ color: "#ef4444" }}> *</span>}
  </label>
);

/* ─── styled text / number input ─── */
const FormInput = ({ style, ...props }) => {
  const base = {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    border: "1.5px solid #e5e7eb", fontSize: 14,
    boxSizing: "border-box", outline: "none",
    background: "#fff", ...style,
  };
  return (
    <input
      style={base}
      onFocus={(e) => (e.target.style.borderColor = "#6362e7")}
      onBlur={(e)  => (e.target.style.borderColor = "#e5e7eb")}
      {...props}
    />
  );
};

/* ════════════════════════════════════════════════════════════════
   EMBEDDED MAP PICKER
════════════════════════════════════════════════════════════════ */
const MapPicker = ({ latitude, longitude, onLocationChange }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef  = useRef(null);
  const markerRef       = useRef(null);

  const lat = parseFloat(latitude) || 20.5937;
  const lng = parseFloat(longitude) || 78.9629;

  /* init map */
  useEffect(() => {
    let isMounted = true;
    ensureLeafletLoaded().then((L) => {
      if (!isMounted || !mapContainerRef.current) return;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      const map = L.map(mapContainerRef.current, { zoomControl: true }).setView([lat, lng], 13);
      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      /* custom purple pin */
      const icon = L.divIcon({
        className: "",
        html: `
          <div style="position:relative;width:32px;height:42px">
            <svg viewBox="0 0 32 42" width="32" height="42" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 0C7.163 0 0 7.163 0 16c0 10.627 14.4 24.747 15.02 25.34a1.39 1.39 0 0 0 1.96 0C17.6 40.747 32 26.627 32 16 32 7.163 24.837 0 16 0z" fill="#6362e7"/>
              <circle cx="16" cy="16" r="7" fill="white"/>
            </svg>
          </div>`,
        iconSize:   [32, 42],
        iconAnchor: [16, 42],
      });

      const marker = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
      markerRef.current = marker;

      /* drag end */
      marker.on("dragend", (e) => {
        const pos = e.target.getLatLng();
        onLocationChange(pos.lat.toFixed(6), pos.lng.toFixed(6));
      });

      /* click on map to move pin */
      map.on("click", (e) => {
        marker.setLatLng(e.latlng);
        onLocationChange(e.latlng.lat.toFixed(6), e.latlng.lng.toFixed(6));
      });
    });

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* keep marker in sync when lat/lng inputs change */
  useEffect(() => {
    const map    = mapInstanceRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const newLat = parseFloat(latitude);
    const newLng = parseFloat(longitude);
    if (!isNaN(newLat) && !isNaN(newLng)) {
      marker.setLatLng([newLat, newLng]);
      map.panTo([newLat, newLng]);
    }
  }, [latitude, longitude]);

  return (
    <div style={{ marginBottom: 16 }}>
      {/* instruction pill */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        marginBottom: 8, fontSize: 11, color: "#6362e7",
        background: "#eff6ff", padding: "5px 12px",
        borderRadius: 20, width: "fit-content",
        border: "1px solid #c7d2fe",
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        Click on the map or drag the pin to set location
      </div>

      {/* map container */}
      <div
        ref={mapContainerRef}
        style={{
          width: "100%", height: 260, borderRadius: 12,
          border: "1.5px solid #e5e7eb", overflow: "hidden",
          zIndex: 0, position: "relative",
        }}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const LocationPage = () => {
  const dispatch = useDispatch();
  const { locations, loading, error, pagination } = useSelector(
    (state) => state.location
  );

  const tableRef = useRef(null);
  const dtRef    = useRef(null);

  /* ── list state ── */
  const [searchTerm, setSearchTerm] = useState("");
  const [page,       setPage]       = useState(1);
  const limit = 10;

  /* ── modal state ── */
  const [showModal,    setShowModal]    = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [locationName, setLocationName] = useState("");
  const [latitude,     setLatitude]     = useState("");
  const [longitude,    setLongitude]    = useState("");
  const [radiusKm,     setRadiusKm]     = useState("");
  const [description,  setDescription]  = useState("");
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState("");
  const [showMap,      setShowMap]      = useState(false);

  /* ── geo-fetch state ── */
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError,   setGeoError]   = useState("");

  /* ── fetch list ── */
  useEffect(() => {
    dispatch(fetchLocations({ page, limit, search: searchTerm }));
  }, [dispatch, page, searchTerm]);

  /* ── DataTable ── */
  useEffect(() => {
    if (!locations.length) return;
    const init = () => {
      if (window.$?.fn?.DataTable) {
        if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
        dtRef.current = window.$(tableRef.current).DataTable({
          responsive: true,
          pageLength: 10,
          columnDefs: [{ orderable: false, targets: [0, 4, 8] }],
        });
      } else {
        setTimeout(init, 300);
      }
    };
    init();
    return () => { if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; } };
  }, [locations]);

  /* ─── helpers ─── */
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  /* ─── geolocation ─── */
  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setGeoLoading(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toFixed(6));
        setLongitude(pos.coords.longitude.toFixed(6));
        setGeoLoading(false);
        setShowMap(true);
      },
      (err) => {
        setGeoError(
          err.code === 1
            ? "Location permission denied. Please allow access."
            : "Unable to fetch location. Try again."
        );
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  /* ─── map location change callback ─── */
  const handleMapLocationChange = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  /* ─── modal open/close ─── */
  const resetForm = () => {
    setLocationName(""); setLatitude(""); setLongitude("");
    setRadiusKm(""); setDescription("");
    setFormError(""); setGeoError(""); setShowMap(false);
  };

  const openAddModal  = () => { setEditItem(null); resetForm(); setShowModal(true); };
  const openEditModal = (item) => {
    setEditItem(item);
    setLocationName(item.locationName);
    setLatitude(String(item.latitude));
    setLongitude(String(item.longitude));
    setRadiusKm(String(item.radiusKm));
    setDescription(item.description || "");
    setFormError(""); setGeoError("");
    setShowMap(true);
    setShowModal(true);
  };
  const handleClose = () => { setShowModal(false); setEditItem(null); resetForm(); };

  /* ─── save ─── */
  const handleSave = async () => {
    if (!locationName.trim())      { setFormError("Location name is required.");          return; }
    if (latitude === "")           { setFormError("Latitude is required.");               return; }
    if (longitude === "")          { setFormError("Longitude is required.");              return; }
    if (radiusKm === "")           { setFormError("Radius (km) is required.");            return; }
    if (isNaN(Number(latitude)))   { setFormError("Latitude must be a valid number.");    return; }
    if (isNaN(Number(longitude)))  { setFormError("Longitude must be a valid number.");   return; }
    if (isNaN(Number(radiusKm)) || Number(radiusKm) < 0) {
      setFormError("Radius must be a non-negative number."); return;
    }

    setSaving(true); setFormError("");
    const data = {
      locationName: locationName.trim(),
      latitude:     parseFloat(latitude),
      longitude:    parseFloat(longitude),
      radiusKm:     parseFloat(radiusKm),
      description:  description.trim(),
    };

    try {
      if (editItem) {
        await dispatch(updateLocation({ id: editItem._id, data })).unwrap();
        dispatch(fetchLocations({ page, limit, search: searchTerm }));
      } else {
        await dispatch(createLocation(data)).unwrap();
        setPage(1);
        dispatch(fetchLocations({ page: 1, limit, search: searchTerm }));
      }
      handleClose();
    } catch (err) {
      setFormError(err || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  /* ─── delete / toggle ─── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await dispatch(deleteLocation(id)).unwrap();
      dispatch(fetchLocations({ page, limit, search: searchTerm }));
    } catch (err) {
      alert(err || "Delete failed.");
    }
  };

  const handleToggle = (id) => dispatch(toggleLocationStatus(id));

  const hasValidCoords =
    latitude && longitude &&
    !isNaN(Number(latitude)) && !isNaN(Number(longitude));

  /* ── derived: is a location already set? ── */
  const locationExists = locations.length > 0;

  /* ════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════════ */
  return (
    <div>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>

          {/* ── Card Header ── */}
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="job-filter">
              <div className="faq-form">
                <input
                  className="form-control" type="text" placeholder="Search.."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round" className="feather feather-search search-icon">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
            </div>

            {/* ── Show Add button only when NO location exists ── */}
            {!locationExists ? (
              <button className="btn btn-primary btn-sm widthbtn" onClick={openAddModal}>
                + Add
              </button>
            ) : (
              <span style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 12, color: "#6362e7",
                background: "#eff6ff", padding: "6px 16px",
                borderRadius: 20, fontWeight: 600,
                border: "1px solid #c7d2fe",
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                Store location set
              </span>
            )}
          </div>

          {/* ── Card Body ── */}
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : error ? (
              <div className="text-danger text-center py-4">{error}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="display table table-bordered" ref={tableRef} style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Si No</th><th>Location Name</th><th>Latitude</th><th>Longitude</th>
                      <th>Radius (km)</th><th>Status</th><th>Created By</th><th>Created Date</th>
                      <th>Updated By</th><th>Updated Date</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: "center", padding: "40px 0" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
                              fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                              <circle cx="12" cy="10" r="3"/>
                            </svg>
                            <span style={{ fontSize: 14, fontWeight: 600, color: "#bbb" }}>No store location set</span>
                            <span style={{ fontSize: 12, color: "#d1d5db" }}>
                              Click "+ Add" to set your store location
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      locations.map((item, index) => (
                        <tr key={item._id}>
                          <td>{(page - 1) * limit + index + 1}</td>
                          <td>{item.locationName}</td>
                          <td><span style={{ fontFamily: "monospace", fontSize: 13 }}>{item.latitude}</span></td>
                          <td><span style={{ fontFamily: "monospace", fontSize: 13 }}>{item.longitude}</span></td>
                          <td>
                            <span style={{ background: "#eff6ff", color: "#6362e7", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                              {item.radiusKm} km
                            </span>
                          </td>
                          <td>
                            <label className="switch">
                              <input type="checkbox" checked={item.status} onChange={() => handleToggle(item._id)}/>
                              <span className="slider round"></span>
                            </label>
                          </td>
                          <td>{item.createdBy || "-"}</td>
                          <td>{formatDate(item.createdAt)}</td>
                          <td>{item.updatedBy || "-"}</td>
                          <td>{formatDate(item.updatedAt)}</td>
                          <td className="d-flex gap-2">
                            <button onClick={() => openEditModal(item)} title="Edit"
                              style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#eff6ff", color: "#6362e7", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "#dbeafe"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "#eff6ff"}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button onClick={() => handleDelete(item._id)} title="Delete"
                              style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "#fff5f5"}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6"/><path d="M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <button key={i + 1}
                    className={`btn btn-sm ${page === i + 1 ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="btn btn-sm btn-outline-secondary" disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          MODAL
      ═══════════════════════════════════════════════════════════ */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div onClick={handleClose} style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(2px)", zIndex: 1040,
          }}/>

          {/* Modal Panel */}
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1050, width: "100%", maxWidth: 560,
            background: "#fff", borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            padding: "32px 32px 28px",
            maxHeight: "92vh", overflowY: "auto",
          }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
                {editItem ? "Edit Location" : "New Location"}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{
                  background: "#eff6ff", color: "#6362e7",
                  fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  padding: "4px 12px", borderRadius: 20, border: "1px solid #6362e7",
                }}>
                  {editItem ? "EDITING" : "ADDING"}
                </span>
                <button onClick={handleClose} style={{
                  width: 32, height: 32, borderRadius: "50%",
                  border: "1px solid #e5e7eb", background: "#f9fafb",
                  cursor: "pointer", fontSize: 16, color: "#555",
                }}>×</button>
              </div>
            </div>

            <hr style={{ borderColor: "#f0f0f0", marginBottom: 20 }}/>

            {/* Error Banner */}
            {formError && (
              <div style={{
                background: "#fef2f2", color: "#dc2626",
                padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
              }}>
                {formError}
              </div>
            )}

            {/* ── Location Name ── */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel required>Location Name</FieldLabel>
              <FormInput
                type="text" value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="e.g. Head Office, Warehouse A"
              />
            </div>

            {/* ── Lat / Lng row ── */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <FieldLabel required>Coordinates</FieldLabel>

                {/* "Use my location" button */}
                <button
                  onClick={fetchCurrentLocation}
                  disabled={geoLoading}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "5px 12px", borderRadius: 20,
                    border: "1.5px solid #6362e7",
                    background: geoLoading ? "#f3f4f6" : "#eff6ff",
                    color: "#6362e7", fontWeight: 600, fontSize: 12,
                    cursor: geoLoading ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) => { if (!geoLoading) e.currentTarget.style.background = "#dbeafe"; }}
                  onMouseLeave={(e) => { if (!geoLoading) e.currentTarget.style.background = "#eff6ff"; }}
                >
                  {geoLoading ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      style={{ animation: "spin 0.8s linear infinite" }}>
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                      <path d="M12 2a10 10 0 0 1 10 10"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                      <path d="M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
                    </svg>
                  )}
                  {geoLoading ? "Detecting…" : "Use My Location"}
                </button>
              </div>

              {/* geo error */}
              {geoError && (
                <div style={{
                  background: "#fff7ed", color: "#c2410c",
                  padding: "8px 12px", borderRadius: 8,
                  marginBottom: 10, fontSize: 12, display: "flex", alignItems: "center", gap: 6,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {geoError}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
                {/* Latitude */}
                <div>
                  <label style={{ display: "block", fontSize: 11, color: "#aaa", marginBottom: 5, fontWeight: 600 }}>Latitude</label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, fontWeight: 700, color: "#9ca3af", pointerEvents: "none" }}>LAT</span>
                    <FormInput
                      type="number" value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                      placeholder="e.g. 12.9716" step="0.000001"
                      style={{ paddingLeft: 42 }}
                    />
                  </div>
                </div>

                {/* Longitude */}
                <div>
                  <label style={{ display: "block", fontSize: 11, color: "#aaa", marginBottom: 5, fontWeight: 600 }}>Longitude</label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, fontWeight: 700, color: "#9ca3af", pointerEvents: "none" }}>LNG</span>
                    <FormInput
                      type="number" value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                      placeholder="e.g. 77.5946" step="0.000001"
                      style={{ paddingLeft: 44 }}
                    />
                  </div>
                </div>
              </div>

              {/* ── Toggle Map Button ── */}
              <button
                onClick={() => setShowMap((v) => !v)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 14px", borderRadius: 20,
                  border: "1.5px solid #6362e7",
                  background: showMap ? "#6362e7" : "#eff6ff",
                  color: showMap ? "#fff" : "#6362e7",
                  fontWeight: 600, fontSize: 12, cursor: "pointer",
                  transition: "all 0.15s", marginBottom: showMap ? 12 : 0,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {showMap ? "Hide Map" : "Pick on Map"}
              </button>

              {/* ── Embedded Map ── */}
              {showMap && (
                <MapPicker
                  latitude={latitude}
                  longitude={longitude}
                  onLocationChange={handleMapLocationChange}
                />
              )}

              {/* Coords display when map is hidden */}
              {!showMap && hasValidCoords && (
                <div style={{
                  marginTop: 8, fontSize: 12, color: "#6b7280",
                  background: "#f9fafb", padding: "6px 12px", borderRadius: 8,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="#6362e7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  Pin set at {Number(latitude).toFixed(4)}, {Number(longitude).toFixed(4)}
                </div>
              )}
            </div>

            {/* ── Radius ── */}
            <div style={{ marginBottom: 16, marginTop: 16 }}>
              <FieldLabel required>Service Radius</FieldLabel>
              <div style={{ position: "relative" }}>
                <FormInput
                  type="number" value={radiusKm}
                  onChange={(e) => setRadiusKm(e.target.value)}
                  placeholder="e.g. 25" min="0" step="0.1"
                  style={{ paddingRight: 52 }}
                />
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 12, fontWeight: 700, color: "#9ca3af", pointerEvents: "none" }}>km</span>
              </div>
              {radiusKm && !isNaN(Number(radiusKm)) && Number(radiusKm) > 0 && (
                <p style={{ margin: "5px 0 0", fontSize: 11, color: "#9ca3af" }}>
                  Covers approximately a {Number(radiusKm)}-km radius around the pin.
                </p>
              )}
            </div>

            {/* ── Description ── */}
            <div style={{ marginBottom: 24 }}>
              <FieldLabel>Description</FieldLabel>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes about this location…"
                rows={2}
                style={{
                  width: "100%", padding: "11px 14px", borderRadius: 10,
                  border: "1.5px solid #e5e7eb", fontSize: 14,
                  boxSizing: "border-box", outline: "none", resize: "vertical",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#6362e7")}
                onBlur={(e)  => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            {/* ── Footer Buttons ── */}
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={handleClose}
                style={{
                  padding: "11px 24px", borderRadius: 10,
                  border: "1.5px solid #e5e7eb", background: "#f9fafb",
                  color: "#555", fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1, padding: "11px 24px", borderRadius: 10, border: "none",
                  background: saving ? "#a5a4f3" : "#6362e7",
                  color: "#fff", fontWeight: 700, fontSize: 14,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving…" : editItem ? "Update Location" : "Save Location"}
              </button>
            </div>
          </div>

          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
      )}
    </div>
  );
};

export default LocationPage;